import Image from 'next/image'
import Link from 'next/link'
import CustomLink from './../CustomLink'
import {ProductURLBuilder} from '@/utils/URLBuilder'
import {toast} from 'react-hot-toast'
import CustomToast from '../CustomToast'
import React from 'react'
import {useMutation, useQueryClient} from 'react-query'
import {WishlistAdd, WishlistDelete} from '@/services/api/wishlist'
import useAuth from '@/hooks/useAuth'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

interface Props {
  product: any
  name?: string
  gender?: string
}
export default function ProductItemList({product, name, gender}: Props) {
  const {t: tAlerts} = useTranslation('alerts');
  const {t: tCommon} = useTranslation('common');
  const WishlistAddMutation = useMutation(WishlistAdd)
  const WishlistRemoveMutation = useMutation(WishlistDelete)
  const queryClient = useQueryClient()
  const {user} = useAuth('none')
  const {locale} = useRouter();
  
  // State for client-side values
  const [clientData, setClientData] = React.useState({
    currency: 'ILS',
    savedChoice: null as { currency: string } | null
  });

  // Initialize client-side data after component mounts
  React.useEffect(() => {
    const currency = localStorage.getItem('user-currency-selection') || 'ILS';
    const savedChoiceString = localStorage.getItem('user-locale-choice');
    const savedChoice = savedChoiceString ? JSON.parse(savedChoiceString) : null;

    setClientData({
      currency,
      savedChoice
    });
  }, []);

  const demoName =
    locale === 'ar' ? product?.demo_name?.ar : product?.demo_name?.en
  const Excerpt =
    locale === 'ar' ? product?.excerpt?.ar : product?.excerpt?.en

  /** ---------------- PRICE LOGIC (NEW) ---------------- */

  const price = product?.price
  const amount = Number(price?.amount ?? 0)
  const symbol = price?.symbol ?? '₪'
  const discount = Number(product?.discount_percentage ?? 0)

  const originalPrice =
    product?.has_sale && discount > 0
      ? Math.round(amount / (1 - discount / 100))
      : null

  /** -------------------------------------------------- */

  const removeWishlist = (id: number) => {
    if (!user) {
      toast.custom(t => (
        <CustomToast t={t} message={tAlerts('login_wishlist')} type="error" />
      ))
      return
    }

    WishlistRemoveMutation.mutate(id, {
      onSuccess() {
        queryClient.invalidateQueries(['wishlist'])
        queryClient.invalidateQueries(['featured_products'])
        queryClient.invalidateQueries(['products'])
        toast.custom(t => (
          <CustomToast t={t} message={tAlerts('wishlist_remove')} type="success" />
        ))
      },
    })
  }

  const addToWishlist = (id: number) => {
    if (!user) {
      toast.custom(t => (
        <CustomToast t={t} message={tAlerts('login_wishlist')} type="error" />
      ))
      return
    }

    WishlistAddMutation.mutate(id, {
      onSuccess() {
        queryClient.invalidateQueries(['wishlist'])
        queryClient.invalidateQueries(['featured_products'])
        queryClient.invalidateQueries(['products'])
        toast.custom(t => (
          <CustomToast t={t} message={tAlerts('wishlist_add')} type="success" />
        ))
      },
    })
  }

  return (
    <div className="product-list-item flex items-center bg-white relative mt-4 p-4 rounded-md">
      <Link
        className="md:h-[200px] md:w-72 w-36 block rounded-md overflow-hidden cursor-pointer"
        href={ProductURLBuilder(
          name ?? (locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en),
          gender,
          product.slug,
          product.product_type,
          clientData.currency
        )}>
        <Image
          src={process.env.NEXT_PUBLIC_CDN_URL + '/' + product.images[0]}
          alt="Book"
          width={200}
          height={200}
          className="w-full h-full hover:scale-110 transition duration-300 rounded-md object-cover"
        />
      </Link>
      <div className={`info flex flex-col justify-between ${locale === 'ar' ? 'mr-4' : 'ml-4'} flex-1`}>
        <h3 className="text-primary text-md sm:text-xl font-semibold">
          <Link
            href={ProductURLBuilder(
              name ?? (locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en),
              gender,
              product.slug,
              product.product_type,
              clientData.currency
            )}>
            <span>
              {product.product_type === 1
                ? demoName.replace('{basmti}', name ?? locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en)
                : demoName.replace('{basmti}', 'ل ' + (name ?? (locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en)))}
            </span>
          </Link>
        </h3>
        <p className="font-light text-text-color mt-2 text-sm sm:text-base">
          {Excerpt
            ? Excerpt.replace('{basmti}', name ?? product.replace_name)
            : ''}
        </p>
        <div className="info-bottom mt-3 flex flex-col">
          <p className="age-group text-success font-semibold">
            {product.has_male && <span>{tCommon('productItemListMale')}</span>}
            {product.has_female && <span>{tCommon('productItemListFemale')}</span>}
          </p>
          <div className='flex items-center justify-start gap-2'>
            <p className="price font-semibold text-xl">
              {symbol} {product?.front_price?.amount}
            </p>
            {product?.has_sale && originalPrice && (
              <p className="price text-red font-line line-through text-md">
                {symbol} {product?.price?.amount}
              </p>
            )}
          </div>
        </div>

        <div className="info-actions flex mt-3 w-full">
          <CustomLink
            href={ProductURLBuilder(
              name ?? product.replace_name,
              gender,
              product.slug,
              product.product_type,
              clientData.currency
            )}
            bg="primary"
            classes={['px-12', 'w-full', ]}>
            <span>{product.product_type === 1 ? 'قم بطلب الكتاب ' : 'قم بطلب الدفاتر '}</span>
          </CustomLink>
          <a
            href="#"
            onClick={e => {
              e.preventDefault()
              product.is_in_wishlist 
                ? removeWishlist(product.id)
                : addToWishlist(product.id)
            }}
            className={`border border-primary rounded-lg ${locale === 'ar' ? 'mr-1 md:mr-4' : 'ml-1 md:ml-4'} flex items-center justify-center p-3`}>
            {product.is_in_wishlist ? (
              <svg
                width="23"
                height="24"
                viewBox="0 0 23 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M20.1357 24C19.7657 23.999 19.3996 23.9247 19.0586 23.7813C18.7175 23.638 18.4083 23.4285 18.1487 23.165L11.9987 17.051L5.84866 23.169C5.45369 23.5697 4.94715 23.8422 4.39508 23.9508C3.843 24.0594 3.27101 23.9992 2.75366 23.778C2.23118 23.5678 1.7842 23.205 1.47112 22.7369C1.15803 22.2688 0.993376 21.7171 0.998658 21.154V5C0.998658 3.67392 1.52544 2.40215 2.46312 1.46447C3.40081 0.526784 4.67258 0 5.99866 0L17.9987 0C18.6553 0 19.3054 0.129329 19.9121 0.380602C20.5187 0.631876 21.0699 1.00017 21.5342 1.46447C21.9985 1.92876 22.3668 2.47996 22.618 3.08658C22.8693 3.69321 22.9987 4.34339 22.9987 5V21.154C23.0043 21.7167 22.8402 22.268 22.5278 22.7361C22.2155 23.2041 21.7694 23.5672 21.2477 23.778C20.8955 23.9253 20.5174 24.0008 20.1357 24Z"
                  fill="#00214D"
                />
              </svg>
            ) : (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_919_2605)">
                  <path
                    d="M20.1371 24C19.7672 23.999 19.4011 23.9247 19.0601 23.7813C18.719 23.638 18.4097 23.4285 18.1501 23.165L12.0001 17.051L5.85012 23.169C5.45515 23.5697 4.94861 23.8422 4.39654 23.9508C3.84447 24.0594 3.27247 23.9992 2.75512 23.778C2.23264 23.5678 1.78567 23.205 1.47258 22.7369C1.15949 22.2688 0.994841 21.7171 1.00012 21.154V5C1.00012 3.67392 1.52691 2.40215 2.46459 1.46447C3.40227 0.526784 4.67404 0 6.00012 0L18.0001 0C18.6567 0 19.3069 0.129329 19.9135 0.380602C20.5202 0.631876 21.0714 1.00017 21.5357 1.46447C22 1.92876 22.3683 2.47996 22.6195 3.08658C22.8708 3.69321 23.0001 4.34339 23.0001 5V21.154C23.0057 21.7167 22.8417 22.268 22.5293 22.7361C22.217 23.2041 21.7709 23.5672 21.2491 23.778C20.8969 23.9253 20.5189 24.0008 20.1371 24ZM6.00012 2C5.20447 2 4.44141 2.31607 3.8788 2.87868C3.31619 3.44129 3.00012 4.20435 3.00012 5V21.154C2.99976 21.3206 3.04879 21.4836 3.14102 21.6224C3.23325 21.7612 3.36455 21.8695 3.51831 21.9337C3.67208 21.9979 3.84143 22.0151 4.00496 21.9831C4.1685 21.9512 4.31888 21.8714 4.43712 21.754L11.3001 14.933C11.4875 14.7468 11.7409 14.6422 12.0051 14.6422C12.2693 14.6422 12.5228 14.7468 12.7101 14.933L19.5651 21.752C19.6834 21.8694 19.8338 21.9492 19.9973 21.9811C20.1608 22.0131 20.3302 21.9959 20.4839 21.9317C20.6377 21.8675 20.769 21.7592 20.8612 21.6204C20.9535 21.4816 21.0025 21.3186 21.0021 21.152V5C21.0021 4.20435 20.6861 3.44129 20.1234 2.87868C19.5608 2.31607 18.7978 2 18.0021 2H6.00012Z"
                    fill="#00214D"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_919_2605">
                    <rect width="24" height="24" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            )}
          </a>
        </div>
      </div>
    </div>
  )
}